import {CustomValidator} from "../../common/validation/types";
import {MusicalWorkValuesType} from "./MusicalWorkCommonStore";
import {actualStatusList, addedStatusList} from "../../common/types";
import {identifierChanged, identifierFeedback} from "../../common/validation/identifierValidation";

export const validateAuthorPercentages: CustomValidator<MusicalWorkValuesType> = {
  name: 'validateAuthorPercentages',
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const activeAuthors = values.showAuthors.filter(x => actualStatusList.includes(x.__status__));

    const totalLyrics = activeAuthors.reduce((sum, author) => sum + Number(author.lyricsPercentage || 0), 0);
    const totalMusic = activeAuthors.reduce((sum, author) => sum + Number(author.musicPercentage || 0), 0);
    const totalPercentage = totalLyrics + totalMusic;

    if (totalPercentage > 100) {
      const message = {
        es: `La suma total de porcentajes de letra (${totalLyrics}%) y música (${totalMusic}%) es ${totalPercentage}%, no puede superar el 100%`,
        en: `The total sum of lyrics (${totalLyrics}%) and music (${totalMusic}%) percentages is ${totalPercentage}%, cannot exceed 100%`,
        pt: `A soma total de porcentagens de letra (${totalLyrics}%) e música (${totalMusic}%) é ${totalPercentage}%, não pode exceder 100%`,
      }[langStore.language] as string;

      errors.authors = {
        message,
        fieldLabel: langStore.MusicalWorkAuthorsLabel
      };
    }

    return { errors, cardErrors };
  }
};

const hasValue = (v: unknown): boolean => v !== null && v !== undefined && v !== "";

/**
 * Espejo cliente (UX) de la regla del server (assertRenewalRequiresDuration): no se
 * puede definir renovación automática sin un límite de duración. Recorre las
 * participaciones por obra (showAuthors[].showPublishers[]).
 */
export const workRenewalRequiresDurationValidator: CustomValidator<MusicalWorkValuesType> = {
  name: 'workRenewalRequiresDurationValidator',
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const activeAuthors = values.showAuthors.filter(x => actualStatusList.includes(x.__status__));
    const conflict = activeAuthors.some(a =>
      (a.showPublishers ?? [])
        .filter(p => actualStatusList.includes(p.__status__))
        .some(p => !hasValue(p.durationInYears) && hasValue(p.autoRenewalYears))
    );

    if (conflict) {
      const message = {
        es: "No se puede definir una renovación automática sin un límite de duración: si la duración es 'Sin límite de duración', la renovación debe ser 'NO'.",
        en: "Automatic renewal can't be set without a duration limit: if the duration is 'No limit', the renewal must be 'NO'.",
        pt: "Não é possível definir renovação automática sem um limite de duração: se a duração é 'Sem limite', a renovação deve ser 'NÃO'.",
      }[langStore.language] as string;
      errors.authors = {
        message,
        fieldLabel: langStore.MusicalWorkAuthorsLabel
      };
    }

    return { errors, cardErrors };
  }
};

/**
 * ISWC de la obra: error si está mal formado o no está en su forma de 11 caracteres
 * (T0345246801). Solo si el campo cambió contra el original. Espejo del server
 * (validateIswcIfChanged). El ISWC no tiene avisos. Puro: no escribe en `values`.
 */
export const musicalWorkIswcValidator: CustomValidator<MusicalWorkValuesType> = {
  name: 'musicalWorkIswcValidator',
  validate(values, langStore, _cache, original) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (identifierChanged(values.iswc, original, "iswc")) {
      const { error } = identifierFeedback("iswc", values.iswc, langStore.language);
      if (error) {
        errors.iswc = { message: error, fieldLabel: langStore.MusicalWorkIswcLabel };
      }
    }

    return { errors, cardErrors };
  }
};

/**
 * ISRC de los fonogramas agregados a la obra. El popup del fonograma ya los valida
 * (phonogramIsrcValidator); esto cubre el ISRC editado en la celda de la grilla, que
 * no pasa ni por el campo ni por el popup. Las filas originales no se revisan acá: la
 * fila no guarda el ISRC anterior, así que no se sabe si cambió, y un dato viejo no
 * tiene que frenar el guardado; si se cambió en la celda, lo rechaza el server, que sí
 * compara con lo guardado. Solo errores: el form de la obra no muestra avisos de la
 * grilla. Puro: no escribe en `values`.
 */
export const musicalWorkPhonogramsIsrcValidator: CustomValidator<MusicalWorkValuesType> = {
  name: 'musicalWorkPhonogramsIsrcValidator',
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const problems = (values.showPhonograms ?? [])
      .filter(row => addedStatusList.includes(row.__status__))
      .map(row => ({
        trackName: row.trackName,
        error: identifierFeedback("isrc", row.isrc, langStore.language).error,
      }))
      .filter(problem => problem.error !== undefined);

    if (problems.length > 0) {
      const phonogram = { es: "Fonograma", en: "Phonogram", pt: "Fonograma" }[langStore.language];
      errors.phonograms = {
        message: problems
          .map(problem => `${phonogram} «${problem.trackName || "—"}»: ${problem.error}`)
          .join(" "),
        fieldLabel: langStore.MusicalWorkPhonogramsLabel,
      };
    }

    return { errors, cardErrors };
  }
};
